<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import type { WiringInstructions, Address } from '@Types/WiringInstrucions.interface';
import KeyValueDetails, {
    type KeyValueSection,
    type RowAction,
} from '@Components/KeyValueDetails.vue';
import { hasAddressContent, resolveCreditToPersonalAddress } from '@Use/useCreditToPersonalAddress';
import useDateOfBirth from '@Use/wiringInstructions/useDateOfBirth';
import { AccountHolderType } from '@Enums/accountHolderType.enum';
import { formatAddressLines } from '@Use/useAddressFormatter';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';

// Import used components
import ConfirmApproveModal from '@Components/modals/ConfirmApproveModal.vue';
import ConfirmRejectModal from '@Components/modals/ConfirmRejectModal.vue';
import ConfirmAddressEditModal from '@Components/confirm/ConfirmAddressEditModal.vue';
import ExpandCollapseToggle from '@Components/ExpandCollapseToggle.vue';
import PlaidConnectModal from '@Components/modals/PlaidConnectModal.vue';
import PlaidPrimingStep from '@Components/shared/PlaidPrimingStep.vue';
import useConfirmSubmit from '@Use/useConfirmSubmit';
import type { SccSubmitResult } from '@Types/SccSubmitResult.interface';
import { FlowLayout } from '@Shared';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { advanceFlow, goBackInFlow } = useFlowNavigation();

const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();
const { maskDateOfBirth, validateDateOfBirth, toIsoDateOfBirth } = useDateOfBirth();

// Page state
const requestDetailsStore = useRequestDetailsStore();
const isNavigating = ref(false);
const isApproveModalOpen = ref(false);
const isRejectModalOpen = ref(false);
const isAddressModalOpen = ref(false);
const isLoading = ref(true);
const errorMessage = ref<string | null>(null);

// Computed
const requestDetail = computed(() => requestDetailsStore.requestDetails);

const blankAddress = (): Address =>
    ({ address1: '', address2: '', city: '', state: '', postalCode: '' });

// The address the screen shows and the modal opens on: a save made earlier in this
// session wins over the server copy, which in turn wins over the bank-sourced one.
const addressToEdit = computed<Address>(() => {
    const persisted = requestSessionStore.confirmPersonalAddress;
    if (hasAddressContent(persisted)) return persisted as Address;
    return resolveCreditToPersonalAddress(requestDetail.value?.wiringInstructions) ?? blankAddress();
});

const openAddressModal = () => {
    isAddressModalOpen.value = true;
};

const editAddressAction: RowAction = {
    label: 'Edit address',
    icon: 'cd-ic-edit',
    dataId: 'confirm-review-edit-address',
    onClick: openAddressModal,
};

const accountDetailsSections = computed<KeyValueSection[]>(() =>
    requestDetail.value?.wiringInstructions
        ? buildWiringSections(
            requestDetail.value.wiringInstructions,
            addressToEdit.value,
            hasAddressContent(addressToEdit.value) ? editAddressAction : undefined,
        )
        : [],
);

// Saving only parks the address in the session store, which is where the read-only
// summary reads it from. Nothing reaches Mongo until Approve finalizes the request.
const onAddressSaved = (address: Address) => {
    requestSessionStore.confirmPersonalAddress = { ...address };
};

// Trimmed on both sides, matching hasAddressContent, so re-saving 'Springfield '
// over 'Springfield' is not treated as an edit and does not trigger a write.
const sameAddress = (a: Address, b: Address | null | undefined): boolean =>
    !!b && (['address1', 'address2', 'city', 'state', 'postalCode'] as const)
        .every(field => (a[field] ?? '').trim() === (b[field] ?? '').trim());

const addressToSubmit = computed<Address | null>(() => {
    const candidate = addressToEdit.value;
    // An all-blank address carries nothing the request does not already have.
    if (!hasAddressContent(candidate)) return null;
    const stored = requestDetail.value?.wiringInstructions?.personalAddress;
    return sameAddress(candidate, stored) ? null : candidate;
});

// ─── Account holder date of birth (PLAT-3373) ────────────────────────────────
// Confirm shows the details the title agent entered; the one thing the agent cannot know is the
// holder's date of birth, so the participant supplies it here before approving.
//
// Shown only when the staff-entered account is explicitly Individual-titled. That is exactly the
// case the BFF forwards a DOB on: a Business account has no natural person, and an account with
// no holder type at all is verified against the request recipient rather than the named holder,
// where a holder DOB would describe the wrong person. Asking for it in either case would be
// friction we then discard.
const accountHolderDateOfBirth = ref('');
const dateOfBirthValidationActive = ref(false);

const isIndividualHolder = computed(() =>
    requestDetail.value?.wiringInstructions?.creditTo?.accountHolderType === AccountHolderType.Individual);

/**
 * PLAT-2857 Phase 2: whether to ask for the date of birth at all. Gated per company and off by
 * default, so this screen looks exactly as it does today until a company is switched on — the
 * field is required to approve, so ungated it would be a new blocker on a live flow.
 */
const shouldCaptureDateOfBirth = computed(() =>
    companyStore.accountValidationCaptureEnabled && isIndividualHolder.value);

const dateOfBirthError = computed<string | null>(() =>
    dateOfBirthValidationActive.value && shouldCaptureDateOfBirth.value
        ? validateDateOfBirth(accountHolderDateOfBirth.value)
        : null);

const onDateOfBirthInput = (val: string) => {
    accountHolderDateOfBirth.value = maskDateOfBirth(val);
};

/** The ISO wire value for the finalize call, or null when this account does not carry one. */
const dateOfBirthToSubmit = computed<string | null>(() =>
    shouldCaptureDateOfBirth.value ? toIsoDateOfBirth(accountHolderDateOfBirth.value) : null);

// Actions
const openApproveModal = () => {
    // Validate before the modal, not inside it: the field lives on this screen, so an error has
    // to be visible next to it rather than behind a dialog the participant just confirmed.
    dateOfBirthValidationActive.value = true;
    if (shouldCaptureDateOfBirth.value && validateDateOfBirth(accountHolderDateOfBirth.value)) return;

    isApproveModalOpen.value = true;
};

const openRejectModal = () => {
    isRejectModalOpen.value = true;
};

// PLAT-2857: accepted-confirm outcomes (blocked / optional Plaid upgrade / success) are
// routed by the composable; the reject path below is untouched.
const {
    plaidLinkToken,
    isPlaidModalOpen,
    isPrimingPlaid,
    continueToPlaid,
    isCompletingPlaid,
    handleAcceptResult,
    onPlaidSuccess,
    onPlaidExit,
} = useConfirmSubmit();
/**
 * The flow's back affordance is hidden while Plaid owns the step (see the template), but the
 * emit is guarded too: by that point the request is finalized, and walking back to edit account
 * details would show the participant a form whose answers can no longer change anything.
 */
const onBackClick = () => {
    if (isPrimingPlaid.value || isCompletingPlaid.value) return;
    goBackInFlow();
};


const onApproved = async (result: SccSubmitResult) => {
    requestSessionStore.confirmRejection = null;
    isNavigating.value = true;
    const stayedForPlaid = await handleAcceptResult(result);
    if (stayedForPlaid) {
        // Still on this page — the Plaid modal is open; the flow advances when it resolves.
        isNavigating.value = false;
    }
};

const onRejected = (data: { reason: string; additionalDetails: string | null }) => {
    requestSessionStore.confirmRejection = {
        reason: data.reason,
        additionalDetails: data.additionalDetails,
    };
    isNavigating.value = true;
    advanceFlow();
};

const buildWiringSections = (
    wi: WiringInstructions,
    creditToPersonalAddress: Address | null,
    addressAction: RowAction | undefined,
): KeyValueSection[] => [
    {
        title: 'credit to',
        rows: [
            {
                type: 'stacked',
                label: 'Account holder',
                values: [
                    wi.creditTo?.accountName,
                    ...formatAddressLines(creditToPersonalAddress),
                ],
                valueWeight: 'normal',
                action: addressAction,
            },
            {
                type: 'labeled',
                label: 'Account number',
                value: wi.creditTo?.accountNumber,
                valueWeight: 'bold',
                sensitive: true,
            },
        ],
    },
    {
        title: 'Final credit to',
        rows: [
            {
                type: 'stacked',
                label: 'Account holder',
                values: [
                    wi.furtherCreditTo?.accountName,
                    ...formatAddressLines(wi.furtherCreditTo?.accountAddress),
                ],
                valueWeight: 'normal',
            },
            {
                type: 'labeled',
                label: 'Account number',
                value: wi.furtherCreditTo?.accountNumber,
                valueWeight: 'bold',
                sensitive: true,
            },
        ],
    },
    {
        title: 'Beneficiary bank',
        rows: [
            { type: 'plain', value: wi.beneficiaryBank?.name, valueWeight: 'normal' },
            {
                type: 'labeled',
                label: 'Routing number',
                value: wi.beneficiaryBank?.routingNumber,
                valueWeight: 'bold',
                sensitive: true,
            },
        ],
    },
];

// Lifecycle
onMounted(async () => {
    isLoading.value = true;
    const result = await requestDetailsStore.fetchRequestDetails();
    if (!result?.success) {
        requestDetailsStore.requestDetails = null;
        errorMessage.value = 'Failed to load account details. Please refresh and try again.';
    }
    isLoading.value = false;
});
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        :show-back-button="!isPrimingPlaid && !isCompletingPlaid"
        @back-click="onBackClick"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <!-- isCompletingPlaid: keep the loader up while the Plaid completion call records the
             attempt, so the review screen never re-appears between Plaid closing and advance. -->
        <app-page-loader v-if="isLoading || isCompletingPlaid" />

        <!-- PLAT-3131: the Plaid offer takes over the step. Shared with Collect — the request is
             already complete and certified by this point, so this is an offer, not another gate. -->
        <plaid-priming-step
            v-else-if="isPrimingPlaid"
            @continue="continueToPlaid"
        />

        <app-form-content-layout v-else class="confirm-review__content">
            <template v-if="errorMessage">
                <p
                    class="confirm-review__error"
                    role="alert"
                >
                    {{ errorMessage }}
                </p>
            </template>
            <template v-if="requestDetail?.wiringInstructions">
                <h1 class="confirm-review__headline">Review account details</h1>

                <p class="confirm-review__description">
                    The funds will be deposited to the bank account below. Please double-check these details before submitting.
                </p>

                <app-collapse
                    class="confirm-review__collapsible"
                    initial-state="opened"
                >
                    <template #trigger="slotProps">
                        <expand-collapse-toggle
                            :is-open="slotProps?.isOpen"
                            open-label="Hide account details"
                            closed-label="Show account details"
                            data-id="confirm-review-account-details-toggle"
                        />
                    </template>
                    <key-value-details :sections="accountDetailsSections" />
                </app-collapse>

                <!-- PLAT-3373: the one detail the title agent cannot supply. Individual-held
                     accounts only; see isIndividualHolder. -->
                <app-text-input
                    v-if="shouldCaptureDateOfBirth"
                    class="confirm-review__date-of-birth"
                    :model-value="accountHolderDateOfBirth"
                    @update:model-value="onDateOfBirthInput"
                    label="Date of birth"
                    placeholder="MM/DD/YYYY"
                    inputmode="numeric"
                    is-required
                    :maxlength="10"
                    :field-status="dateOfBirthError ? 'error' : null"
                    :message="dateOfBirthError ?? ''"
                    data-id="confirm-review-date-of-birth"
                >
                    <template #focus-message>
                        <span v-if="!accountHolderDateOfBirth">Enter the date of birth of the bank account owner, as it appears on their government-issued ID.</span>
                    </template>
                </app-text-input>
            </template>
        </app-form-content-layout>
        
        <!-- Hidden while the Plaid offer or its completion owns the step: the request is already
             approved by then, and leaving Approve/Reject live underneath would let a participant
             reject something they have already approved. CollectReview gates its button the same way. -->
        <div
            v-if="!errorMessage && requestDetail?.wiringInstructions && !isPrimingPlaid && !isCompletingPlaid"
            class="confirm-review__buttons"
        >
            <app-button
                class="confirm-review__button confirm-review__button--approve"
                expanded
                type="green"
                size="large"
                :is-loading="isNavigating"
                data-id="confirm-review-approve"
                @onClick="openApproveModal"
            >
                Approve
            </app-button>
            <app-button
                class="confirm-review__button confirm-review__button--reject"
                expanded
                size="large"
                type="red"
                :is-loading="isNavigating"
                data-id="confirm-review-reject"
                @onClick="openRejectModal"
            >
                Reject
            </app-button>

        </div>
    </flow-layout>

    <confirm-approve-modal
        v-model="isApproveModalOpen"
        :personal-address="addressToSubmit"
        :account-holder-date-of-birth="dateOfBirthToSubmit"
        @approved="onApproved" />
    <confirm-reject-modal
        v-model="isRejectModalOpen"
        @rejected="onRejected" />
    <confirm-address-edit-modal
        v-model="isAddressModalOpen"
        :address="addressToEdit"
        @save="onAddressSaved" />
    <plaid-connect-modal
        v-if="isPlaidModalOpen && plaidLinkToken"
        v-model="isPlaidModalOpen"
        :link-token="plaidLinkToken"
        @on-success="onPlaidSuccess"
        @on-exit="onPlaidExit" />
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;
@use "sass:color";

.confirm-review {
    &__content {
        margin-bottom: 2rem;
    }

    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__buttons {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        // Override the green app button color for uilib
        :deep(.app-button) {
            &.green {
                background-color: #00A566;

                &:hover {
                    background-color: color.adjust(#00A566, $lightness: -10%);
                    cursor: pointer;
                }
            }
        }
    }

    &__collapsible {
        display: flex;
        flex-direction: column;

        // this has to be locally scoped for now
        :deep(.trigger-indicator) {
            display: none;
        }
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }
}
</style>